import React from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Progress, 
  Segmented,
  Badge,
  Tooltip
} from 'antd';
import { 
  PlusOutlined, 
  CheckCircleOutlined, 
  DeleteOutlined,
  OrderedListOutlined
} from '@ant-design/icons';
import { DndContext } from '@dnd-kit/core';
import SubtaskItem from './SubtaskItem';
import { calculateSubtaskProgress } from '../utils';

const { Text } = Typography;

const SubtaskManager = ({
  subtasks = [],
  newSubtask = '',
  onNewSubtaskChange,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onEditSubtask,
  onClearCompleted,
  onMarkAllComplete,
  onMarkAllIncomplete,
  activeTab = 'all',
  onTabChange,
  style = {}
}) => {
  const progress = calculateSubtaskProgress(subtasks);
  const completedCount = subtasks.filter(s => s.completed).length;
  const totalCount = subtasks.length;

  const getFilteredSubtasks = () => {
    switch (activeTab) {
      case 'active':
        return subtasks.filter(subtask => !subtask.completed);
      case 'completed':
        return subtasks.filter(subtask => subtask.completed);
      default:
        return subtasks;
    }
  };

  const filteredSubtasks = getFilteredSubtasks();

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <OrderedListOutlined style={{ color: '#1890ff' }} />
          <span>Checklist & Subtasks</span>
          {subtasks.length > 0 && (
            <Badge 
              count={`${completedCount}/${totalCount}`} 
              style={{ 
                marginLeft: '8px',
                background: progress === 100 ? '#52c41a' : '#1890ff'
              }} 
            />
          )}
        </div>
      }
      extra={
        subtasks.length > 0 && (
          <Space>
            <Tooltip title="Mark All Complete">
              <Button 
                size="small" 
                icon={<CheckCircleOutlined />}
                onClick={onMarkAllComplete}
                disabled={progress === 100}
              >
                Complete All
              </Button>
            </Tooltip>
            <Tooltip title="Clear Completed">
              <Button 
                size="small" 
                danger
                icon={<DeleteOutlined />}
                onClick={onClearCompleted}
                disabled={completedCount === 0}
              >
                Clear Done
              </Button>
            </Tooltip>
          </Space>
        )
      }
      style={{ marginBottom: '20px', ...style }}
      bodyStyle={{ padding: '16px' }}
    >
      {/* Progress Overview */}
      {subtasks.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <Text strong>Progress Overview</Text>
            <Text strong style={{ color: progress === 100 ? '#52c41a' : '#1890ff', fontSize: '16px' }}>
              {progress}%
            </Text>
          </div>
          <Progress 
            percent={progress} 
            strokeColor={{
              '0%': '#1890ff',
              '100%': '#52c41a',
            }}
            style={{ marginBottom: '8px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {completedCount} of {totalCount} tasks completed
            </Text>
            {progress === 100 && (
              <Badge color="success" icon={<CheckCircleOutlined />}>
                All Complete!
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Subtasks Tabs */}
      {subtasks.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <Segmented
            options={[
              { label: `All (${totalCount})`, value: 'all' },
              { label: `Active (${totalCount - completedCount})`, value: 'active' },
              { label: `Completed (${completedCount})`, value: 'completed' },
            ]}
            value={activeTab}
            onChange={onTabChange}
            style={{ 
              width: '100%',
              background: '#f5f5f5',
              borderRadius: '6px',
              padding: '4px'
            }}
          />
        </div>
      )}

      {/* Add Subtask Input */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <Input
          placeholder="Add a new subtask..."
          value={newSubtask}
          onChange={(e) => onNewSubtaskChange(e.target.value)}
          size="middle"
          style={{ flex: 1, borderRadius: '6px' }}
          onPressEnter={onAddSubtask}
          prefix={<PlusOutlined style={{ color: '#1890ff' }} />}
        />
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={onAddSubtask}
          disabled={!newSubtask.trim()}
          style={{ borderRadius: '6px' }}
        >
          Add
        </Button>
      </div>

      {/* Subtasks List */}
      <DndContext>
        <div style={{ 
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '4px'
        }}>
          {filteredSubtasks.length > 0 ? (
            filteredSubtasks.map((subtask, index) => (
              <SubtaskItem
                key={subtask.id}
                subtask={subtask}
                index={index}
                onToggle={onToggleSubtask}
                onDelete={onDeleteSubtask}
                onEdit={onEditSubtask}
              />
            ))
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#bfbfbf'
            }}>
              {activeTab === 'all' ? (
                <>
                  <OrderedListOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                  <div style={{ fontSize: '16px', marginBottom: '8px', color: '#8c8c8c' }}>
                    No subtasks yet
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Add subtasks to break down this task into smaller, manageable steps
                  </Text>
                </>
              ) : activeTab === 'active' ? (
                <>
                  <CheckCircleOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                  <div style={{ fontSize: '16px', marginBottom: '8px', color: '#8c8c8c' }}>
                    No active subtasks
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    All subtasks are completed! Great job! 🎉
                  </Text>
                </>
              ) : (
                <>
                  <CheckCircleOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5, color: '#52c41a' }} />
                  <div style={{ fontSize: '16px', marginBottom: '8px', color: '#8c8c8c' }}>
                    No completed subtasks yet
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Start checking off subtasks to track your progress
                  </Text>
                </>
              )}
            </div>
          )}
        </div>
      </DndContext>

      {/* Quick Actions */}
      {subtasks.length > 0 && (
        <div style={{ 
          marginTop: '16px',
          padding: '12px 16px',
          background: '#f0f9ff',
          borderRadius: '6px',
          border: '1px solid #91d5ff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 <strong>Quick Actions:</strong> Double-click to edit • Drag to reorder
            </Text>
            <Space size={8}>
              <Button 
                size="small" 
                onClick={onMarkAllIncomplete}
                disabled={completedCount === 0}
              >
                Mark All Active
              </Button>
              <Button 
                size="small" 
                type="primary"
                onClick={onMarkAllComplete}
                disabled={progress === 100}
              >
                Complete All
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SubtaskManager;
